import Image from 'next/image';
import Router from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackClickedPublicBoardLink } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { AssetModalHeaderAirLogoLink } from '~/components/AssetModalV2/Header/components/AssetModalHeaderAirLogoLink';
import { PublicTopBarAccountInfo } from '~/components/Layouts/PublicBoardLayout/PublicTopBarAccountInfo';
import { SignupModal } from '~/components/Modals/SignupModal/SignUpModal';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { WorkspaceAvatar } from '~/components/WorkspaceAvatarAndName';
import { usePublicCustomLogo } from '~/hooks/usePublicCustomLogo';
import { useShowPublicAirLogo } from '~/hooks/useShowPublicAirLogo';
import { useShowPublicSignUpButton } from '~/hooks/useShowPublicSignUpButton';
import { useAccountContext } from '~/providers/AccountProvider';

export const DefaultBanner = memo(() => {
  const { workspaceImage, workspaceName } = usePublicWorkspace();
  const { customLogoUrl } = usePublicCustomLogo();
  const { data: account } = useAccountContext();
  const [showSignUpModal] = useAirModal(SignupModal);
  const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();

  const { showAirLogo } = useShowPublicAirLogo();
  const { showSignUpButton } = useShowPublicSignUpButton();

  const onSignUpClick = useCallback(() => {
    showSignUpModal({
      accountCreationSource: 'share-link',
      ssoCallbackData: {
        url: Router.asPath,
      },
    });
    trackClickedPublicBoardLink({
      name: 'clicked login',
      page_area: 'banner',
      page_type: 'public board',
    });
  }, [showSignUpModal, trackClickedPublicBoardLink]);

  return (
    <div
      className="relative flex h-14 w-full shrink-0 items-center justify-between ads:bg-base bg-pigeon-900 px-2"
      data-testid="PUBLIC_ASSET_MOBILE_DEFAULT_BANNER"
    >
      <div className="flex items-center justify-start" data-testid="PUBLIC_ASSET_MOBILE_WORKSPACE_INFO">
        {customLogoUrl ? (
          // Custom brand marks are frequently wide (horizontal wordmarks), so the logo renders
          // whole at the avatar slot's height with auto width instead of going through
          // WorkspaceAvatar's square crop (the fallback branch below keeps the square avatar).
          // Rendered locally rather than changing the shared WorkspaceAvatar, which private
          // surfaces use as a square avatar. max-w bracket is a proto-fidelity cap
          // (mattq/proto-presentation-mode) — no configured max-w steps exist near it.
          // `unoptimized` skips the next/image resizer, whose fixed-size renditions would soften
          // wide marks scaled by CSS; height/width are only the intrinsic-ratio hint.
          <Image
            alt={`Logo for ${workspaceName}`}
            className="h-8 w-auto max-w-[160px] shrink-0 object-contain object-left"
            data-testid="PUBLIC_CUSTOM_LOGO"
            height={32}
            src={customLogoUrl}
            unoptimized
            width={192}
          />
        ) : (
          <>
            <WorkspaceAvatar image={workspaceImage} name={workspaceName} />
            <p className="mx-2 truncate font-medium ads:text-secondary text-16 text-gray-11">{workspaceName}</p>
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-4">
        {account ? (
          <PublicTopBarAccountInfo account={account} />
        ) : (
          showSignUpButton && (
            <button
              className="cursor-pointer px-0.5 font-semibold ads:text-metadata text-14 text-gray-10 hover:underline"
              data-testid="PUBLIC_VIEWER_SIGN_UP_CTA"
              onClick={onSignUpClick}
              type="button"
            >
              Sign up
            </button>
          )
        )}
        {showAirLogo && <AssetModalHeaderAirLogoLink />}
      </div>
    </div>
  );
});

DefaultBanner.displayName = 'DefaultBanner';
